import { SCAN_PROCESS_STEPS, MAIN_PROCESS_STEPS } from './flowConfig';

/**
 * Clinical / UX help aligned with product spec (Help Tip, Function, When to Use, etc.)
 * Keys are editor tool ids from constants/tools.js where applicable.
 */

export const TOOL_HELP = {
  // —— Preparing Scan ——
  scan_preparing_scissors: {
    iconName: 'Scissors (Trim Tool)',
    function: 'Removes unwanted scan regions.',
    whenToUse: 'Immediately after importing scan.',
    helpTip:
      'Draw boundary and remove excess scan areas such as floor or background mesh.',
    commonMistake: 'Accidentally trimming anatomy.',
    clinicalNotes: 'Zoom before confirming trim.',
  },
  scan_preparing_eraser: {
    iconName: 'Eraser',
    function: 'Deletes small scan noise.',
    whenToUse: 'After trimming.',
    helpTip: 'Delete floating particles or minor unwanted mesh artifacts.',
    commonMistake: 'Over-erasing main scan.',
    clinicalNotes: 'Use small brush size.',
  },
  scan_preparing_pencil: {
    iconName: 'Fill Hole',
    function: 'Closes scan surface gaps.',
    whenToUse: 'When small holes visible.',
    helpTip: 'Automatically fills minor open surfaces to create a watertight mesh.',
    commonMistake: 'Filling large anatomical gaps.',
    clinicalNotes: 'Large holes may indicate incomplete scan.',
  },
  scan_preparing_measure: {
    iconName: 'Patient Measurements',
    function: 'Shows patient measurements.',
    whenToUse: 'Before modification begins.',
    helpTip:
      'Displays assessment form measurements for cross-verification with scan geometry.',
    commonMistake:
      'Ignoring mismatch to large extent between scan and recorded measurements. Note: minor variations are acceptable between hand measurements and scan measurements.',
    clinicalNotes: 'Mismatch may indicate scan scaling error.',
  },
  scan_preparing_circumference: {
    iconName: 'Key Measurements',
    function: 'Shows validation measurements.',
    whenToUse: 'Before rectification.',
    helpTip:
      'Displays essential dimensions required before proceeding to socket design validation.',
    commonMistake: 'Proceeding without dimension verification.',
    clinicalNotes: 'Incorrect dimensions affect final socket fit.',
  },
  main_rectification_circumference_lines: {
    iconName: 'Circumference Lines',
    function: 'Measures the socket at each line, original scan beside the current socket.',
    whenToUse: 'After a circumference modification, and any time the socket changes afterwards.',
    helpTip:
      'Drag a ring on the socket, or edit Falloff, to move a line; Scan and Mod re-measure at the new height.',
    commonMistake: 'Reading a stale value — the line must sit at the height you mean to report.',
    clinicalNotes: 'Reporting only; moving a line never reshapes the socket.',
  },
  scan_preparing_move: {
    iconName: 'Move Socket',
    function: 'Repositions the model in the scene.',
    whenToUse: 'During scan inspection and cleanup.',
    helpTip: 'Use the gizmo to place the limb for comfortable trimming and measurement.',
    commonMistake: 'Working at an awkward angle without repositioning first.',
    clinicalNotes: 'Stable, repeatable orientation supports consistent clinical checks.',
  },
  scan_preparing_rotate: {
    iconName: 'Rotate Socket',
    function: 'Reorients the model in the scene.',
    whenToUse: 'During scan inspection and cleanup.',
    helpTip: 'Rotate until distal and posterior surfaces are easy to see and edit.',
    commonMistake: 'Inspecting only one view and missing defects.',
    clinicalNotes: 'Always inspect distal and posterior regions carefully.',
  },
  scan_preparing_lasso: {
    iconName: 'Lasso Selection',
    function: 'Selects regions on the scan for targeted edits.',
    whenToUse: 'When you need a freehand selection before other operations.',
    helpTip: 'Close the lasso carefully around the intended region; zoom in for accuracy.',
    commonMistake: 'Loose selections that include anatomy you meant to keep.',
    clinicalNotes: 'Verify selection edges against clinical landmarks.',
  },

  // —— Axis & Alignment ——
  scan_axes_pin: {
    iconName: 'Axis Tool',
    function: 'Defines anatomical axis.',
    whenToUse: 'Before rectification.',
    helpTip:
      'Mark anatomical reference points to establish mechanical alignment. When all landmarks are placed, press Apply alignment in the panel, then adjust views and transforms as needed.',
    commonMistake: 'Incorrect point placement.',
    clinicalNotes: 'Improper axis affects gait alignment.',
  },
  scan_axes_movement: {
    iconName: 'Alignment Tool',
    function: 'Aligns model in 3D space.',
    whenToUse: 'After axis definition.',
    helpTip: 'Automatically aligns model based on selected anatomical axis points.',
    commonMistake: 'Skipping alignment.',
    clinicalNotes: 'Alignment ensures proper load distribution.',
  },

  // —— Rectification ——
  main_rectification_blocking_zone: {
    iconName: 'Blocking Zone',
    function: 'Locks region from modification.',
    whenToUse: 'When preserving anatomical areas.',
    helpTip: 'Prevents modifications in selected anatomical zones.',
    commonMistake: 'Forgetting to unblock.',
    clinicalNotes: 'Use for protected load areas.',
  },
  main_rectification_measure_reduction: {
    iconName: 'Circumference Modification',
    function: 'Local volume control.',
    whenToUse: 'For tight or loose areas.',
    helpTip: 'Increase or decrease circumference locally for targeted fit adjustment.',
    commonMistake: 'Excessive reduction.',
    clinicalNotes: 'Check cross-section after applying.',
  },
  main_rectification_global_reduction: {
    iconName: 'Global Reduction',
    function: 'Uniform volume adjustment.',
    whenToUse: 'When overall fit needs correction.',
    helpTip: 'Applies uniform addition or reduction across entire model.',
    commonMistake: 'Using instead of local modification.',
    clinicalNotes: 'Use cautiously in bony limbs.',
  },
  main_rectification_scoop_recharge: {
    iconName: 'Carve Relief',
    function: 'Localized material removal.',
    whenToUse: 'Over tibial crest or fibular head.',
    helpTip: 'Remove material locally over bony prominences to reduce pressure.',
    commonMistake: 'Over-carving.',
    clinicalNotes: 'Balance relief with load stability.',
  },
  main_rectification_inflate_deflate: {
    iconName: 'Inflate / Deflate',
    function: 'Freehand local volume addition or removal.',
    whenToUse: 'To fine-tune relief right after carving or reducing.',
    helpTip: 'Drag on the socket to inflate; hold Ctrl to deflate.',
    commonMistake: 'Heavy strokes that leave a visible bulge or dent.',
    clinicalNotes: 'Re-check circumference after adjusting volume by hand.',
  },
  main_rectification_sculpt_smooth: {
    iconName: 'Sculpt Smooth',
    function: 'Freehand, real-time surface smoothing.',
    whenToUse: 'On a single rough spot the painted-zone brush handles too broadly.',
    helpTip: 'Drag on the socket to smooth as you go; hold Ctrl to sharpen instead.',
    commonMistake: 'Long passes that flatten the surrounding contour.',
    clinicalNotes: 'Keep strokes local so adjacent load-bearing shape is untouched.',
  },
  main_rectification_global_smooth: {
    iconName: 'Global Smoothing',
    function: 'Relaxes the whole surface in one pass.',
    whenToUse: 'After the rectification edits, when the socket needs an overall clean-up rather than a local one.',
    helpTip:
      'Raise the intensity for more Laplacian passes; keep "Lock volume" on so the socket does not shrink.',
    commonMistake: 'High intensity, which washes out the reduction and relief detail everywhere.',
    clinicalNotes: 'Check load-bearing contours and circumference after applying.',
  },
  main_rectification_carve_smoothning: {
    iconName: 'Smoothening',
    function: 'Surface blending after carve relief.',
    whenToUse: 'After carving reliefs or reductions.',
    helpTip: 'Shift + drag to paint zones, then smoothen the transitions between modified areas.',
    commonMistake: 'Over-smoothing contours.',
    clinicalNotes: 'Maintain PTB load definition.',
  },
  main_rectification_smoothning: {
    iconName: 'Smoothening',
    function: 'Surface blending.',
    whenToUse: 'After rectification.',
    helpTip: 'Smoothens sharp edges and transitions between modified zones.',
    commonMistake: 'Over-smoothing contours.',
    clinicalNotes: 'Maintain PTB load definition.',
  },

  // —— Elongation ——
  main_elongation_elongation: {
    iconName: 'Elongation Tool',
    function: 'Vertical extension control.',
    whenToUse: 'When extra height required.',
    helpTip: 'Extends proximal region vertically to adjust socket height.',
    commonMistake: 'Over-elongation.',
    clinicalNotes: 'Recheck trim line after elongation.',
  },

  // —— Trim lines ——
  main_trimline_trimline: {
    iconName: 'Trim Line Tool',
    function: 'Defines socket trim limits.',
    whenToUse: 'After rectification complete.',
    helpTip: 'Draw and refine proximal and distal trim lines for final socket shape.',
    commonMistake: 'Uneven trim line.',
    clinicalNotes: 'Ensure smooth medial and lateral contour.',
  },

  // —— Flares ——
  main_flares_flare: {
    iconName: 'Flare Tool',
    function: 'Creates socket edge flare.',
    whenToUse: 'After trim lines defined.',
    helpTip: 'Expands trim edge outward to improve comfort and reduce edge pressure.',
    commonMistake: 'Excessive flare.',
    clinicalNotes: 'Ensure smooth edge transition.',
  },
  main_flares_smoothning: {
    iconName: 'Flares Smoothening',
    function: 'Surface blending at flare transitions.',
    whenToUse: 'After defining flares.',
    helpTip: 'Smoothens sharp edges and transitions between flare and socket body.',
    commonMistake: 'Over-smoothing contours.',
    clinicalNotes: 'Maintain comfortable edge relief without losing trim definition.',
  },
  main_flares_global_smooth: {
    iconName: 'Global Smoothing',
    function: 'Relaxes the whole surface in one pass.',
    whenToUse: 'After the flare is applied, when the socket needs an overall clean-up rather than a local one.',
    helpTip: 'Raise the intensity for more Laplacian passes; keep "Lock volume" on so the socket does not shrink.',
    commonMistake: 'High intensity, which washes out rectification detail everywhere, not just at the flare.',
    clinicalNotes: 'Check load-bearing contours and circumference after applying.',
  },

  // —— Thickness ——
  main_thickness_thickness: {
    iconName: 'Thickness Tool',
    function: 'Controls wall thickness.',
    whenToUse: 'After trim and flare complete.',
    helpTip: 'Defines uniform or variable wall thickness for socket strength.',
    commonMistake: 'Too thin causing breakage.',
    clinicalNotes: 'Adjust based on patient weight/activity level.',
  },

  // —— ML Reduction (not in original table) ——
  main_ml_reduction_brush: {
    iconName: 'ML reduction',
    function: 'Medio-lateral reduction via brushed vertex regions.',
    whenToUse: 'When selective wall reduction is required.',
    helpTip:
      'Hold Shift and drag on the mesh (Draw/Erase); paint region A then B. Apply reduction runs once per round until you Undo (Undo restores geometry and lets you Apply again).',
    commonMistake: 'Aggressive reduction without checking load-bearing areas.',
    clinicalNotes: 'Balance cosmetic reduction with structural and comfort requirements.',
  },
  main_ml_reduction_smoothning: {
    iconName: 'ML Reduction Smoothing',
    function: 'Surface blending after ML reduction.',
    whenToUse: 'After applying ML reduction.',
    helpTip: 'Smoothens sharp edges and transitions at reduced regions. Independent from rectification smoothing.',
    commonMistake: 'Over-smoothing contours.',
    clinicalNotes: 'Maintain load-bearing definition while softening transition zones.',
  },

  // —— Other main tools (spec table did not list) ——
  main_locking_lock: {
    iconName: 'Locking System',
    function: 'Defines locking mechanism geometry on the socket.',
    whenToUse: 'When the design includes a lock.',
    helpTip: 'Position and orient the lock for donning, doffing, and clearance.',
    commonMistake: 'Interference with trim line or suspension components.',
    clinicalNotes: 'Confirm clinical clearance with the intended component.',
  },
  main_valve_valve: {
    iconName: 'Valve',
    function: 'Adds or positions the valve feature.',
    whenToUse: 'When vacuum or expulsion valve is in the prescription.',
    helpTip: 'Place the valve for access and reliable seal with the liner system.',
    commonMistake: 'Valve too close to trim edge or under load-bearing areas.',
    clinicalNotes: 'Match valve choice to activity level and suspension.',
  },
  main_pads_pad: {
    iconName: 'Pad',
    function: 'Adds comfort or relief padding regions.',
    whenToUse: 'When local pressure relief is prescribed.',
    helpTip: 'Size pads to the prominence without eliminating needed support.',
    commonMistake: 'Pads that shift load into a painful area.',
    clinicalNotes: 'Reconcile pad placement with PTB and trim height.',
  },
  main_finalization_review: {
    iconName: 'Review',
    function: 'Final visual check of the completed design.',
    whenToUse: 'Before export.',
    helpTip: 'Rotate through views and confirm trim, thickness, and features.',
    commonMistake: 'Skipping a systematic check of distal and posterior areas.',
    clinicalNotes: 'Treat review as the last clinical safety gate before fabrication.',
  },
  main_finalization_export: {
    iconName: 'Export',
    function: 'Exports the design for manufacturing.',
    whenToUse: 'After final approval.',
    helpTip: 'Export the correct file type and units expected by your CAM workflow.',
    commonMistake: 'Exporting before saving the final thickness and trim state.',
    clinicalNotes: 'Keep a versioned record aligned with the signed design.',
  },
  main_finalization_save: {
    iconName: 'Save',
    function: 'Persists the current design state.',
    whenToUse: 'After meaningful changes.',
    helpTip: 'Save before switching steps or closing the session.',
    commonMistake: 'Relying on a single save at the end of a long session.',
    clinicalNotes: 'Frequent saves protect against data loss between clinical reviews.',
  },
};

/** UI affordances on the canvas / layout, keyed by workflow step */
export const STEP_EXTRA_HELP = {
  [SCAN_PROCESS_STEPS.PREPARING_SCAN]: [
    {
      id: 'view_orientation',
      iconName: 'View Orientation Widget',
      function: 'Changes viewing angles of 3D model.',
      whenToUse: 'During scan inspection and cleanup.',
      helpTip:
        'Rotate the model to predefined orientations (Front, Back, Left, Right, Top, Bottom) to inspect all anatomical surfaces.',
      commonMistake: 'Inspecting only one view and missing defects.',
      clinicalNotes: 'Always inspect distal and posterior regions carefully.',
    },
    {
      id: 'ghost_view',
      iconName: 'Ghost View',
      function: 'Enables transparency mode.',
      whenToUse: 'When verifying alignment or thickness preview.',
      helpTip: 'Displays semi-transparent model for viewing internal geometry and alignment.',
      commonMistake: 'Misjudging surface quality in ghost mode.',
      clinicalNotes: 'Alternate with solid mode for clarity.',
    },
    {
      id: 'color_mode',
      iconName: 'Color Mode',
      function: 'Enables solid visualization.',
      whenToUse: 'For identifying scan noise and spikes.',
      helpTip: 'Displays model in solid shaded mode for surface inspection.',
      commonMistake: 'Missing hidden artifacts.',
      clinicalNotes: 'Rotate under light for clarity.',
    },
  ],
  [SCAN_PROCESS_STEPS.AXES_ALIGNMENT]: [
    {
      id: 'undo',
      iconName: 'Undo',
      function: 'Undo last action.',
      whenToUse: 'When incorrect change applied.',
      helpTip: 'Reverts previous modification step.',
      commonMistake: 'Not undoing early.',
      clinicalNotes: 'Save frequently.',
    },
    {
      id: 'redo',
      iconName: 'Redo',
      function: 'Redo last undone step.',
      whenToUse: 'When undo performed accidentally.',
      helpTip: 'Reapplies previously undone action.',
      commonMistake: 'Redo without review.',
      clinicalNotes: 'Confirm visually after redo.',
    },
    {
      id: 'fullscreen',
      iconName: 'Full Screen',
      function: 'Expands viewing area.',
      whenToUse: 'During fine inspection.',
      helpTip: 'Maximizes design window for detailed inspection.',
      commonMistake: 'Forgetting to exit full screen.',
      clinicalNotes: 'Use during trim and rectification.',
    },
  ],
  [MAIN_PROCESS_STEPS.RECTIFICATION]: [
    {
      id: 'load_draft',
      iconName: 'Load Draft',
      function: 'Load previous state.',
      whenToUse: 'When resuming work.',
      helpTip: 'Loads previously saved draft of current design.',
      commonMistake: 'Loading outdated draft.',
      clinicalNotes: 'Confirm version before editing.',
    },
    {
      id: 'save_draft',
      iconName: 'Save Draft',
      function: 'Save design progress.',
      whenToUse: 'After major edits.',
      helpTip: 'Saves current stage of modification.',
      commonMistake: 'Not saving frequently.',
      clinicalNotes: 'Save before next page.',
    },
  ],
  [MAIN_PROCESS_STEPS.ELONGATION]: [
    {
      id: 'load_draft',
      iconName: 'Load Draft',
      function: 'Load saved version.',
      whenToUse: 'When returning to previous stage.',
      helpTip: 'Loads previously saved elongation stage.',
      commonMistake: 'Overwriting correct version.',
      clinicalNotes: 'Save before experimentation.',
    },
    {
      id: 'save_draft',
      iconName: 'Save Draft',
      function: 'Save elongation edits.',
      whenToUse: 'After height adjustment.',
      helpTip: 'Saves model after elongation adjustments.',
      commonMistake: 'Forgetting to save.',
      clinicalNotes: 'Save before trim stage.',
    },
  ],
  [MAIN_PROCESS_STEPS.TRIMLINE]: [
    {
      id: 'load_draft',
      iconName: 'Load Draft',
      function: 'Load trim state.',
      whenToUse: 'When re-editing trim.',
      helpTip: 'Loads previously saved trim configuration.',
      commonMistake: 'Editing wrong draft.',
      clinicalNotes: 'Verify before modifying.',
    },
    {
      id: 'save_draft',
      iconName: 'Save Draft',
      function: 'Save trim edits.',
      whenToUse: 'After trim confirmation.',
      helpTip: 'Saves defined trim lines.',
      commonMistake: 'Not saving final trim.',
      clinicalNotes: 'Save before thickness stage.',
    },
  ],
  [MAIN_PROCESS_STEPS.THICKNESS]: [
    {
      id: 'socket_type',
      iconName: 'Socket Type Selection',
      function: 'Select socket configuration.',
      whenToUse: 'Before wall thickness application.',
      helpTip: 'Select appropriate socket configuration before thickness application.',
      commonMistake: 'Selecting wrong type.',
      clinicalNotes: 'Socket type impacts strength.',
    },
    {
      id: 'load_draft',
      iconName: 'Load Draft',
      function: 'Load thickness stage.',
      whenToUse: 'When re-editing.',
      helpTip: 'Loads previously saved thickness setup.',
      commonMistake: 'Overwriting final version.',
      clinicalNotes: 'Confirm before saving.',
    },
    {
      id: 'save_draft',
      iconName: 'Save Draft',
      function: 'Save final design.',
      whenToUse: 'Before export.',
      helpTip: 'Saves current thickness configuration.',
      commonMistake: 'Forgetting final save.',
      clinicalNotes: 'Always save before exporting STL.',
    },
  ],
};

const STEP_OVERVIEW_FALLBACK = {
  [SCAN_PROCESS_STEPS.FINAL_DESIGN_PREVIEW]: {
    iconName: 'Preview',
    function: 'Review the prepared scan before the main socket workflow.',
    whenToUse: 'After scan preparation and alignment.',
    helpTip:
      'Confirm geometry, scale, and obvious defects before continuing to rectification.',
    commonMistake: 'Advancing with an unverified scan.',
    clinicalNotes: 'Resolve measurement mismatches before clinical sign-off.',
  },
  [MAIN_PROCESS_STEPS.CLINICAL_MODIFICATION_PREVIEW]: {
    iconName: 'Preview',
    function: 'Review clinical socket changes before alignment and locking.',
    whenToUse: 'After thickness and other clinical modification steps.',
    helpTip: 'Confirm contours, trim, and wall thickness before alignment.',
    commonMistake: 'Skipping a full visual review of the modified socket.',
    clinicalNotes: 'Treat this as the last gate before alignment and locking.',
  },
};

/**
 * @param {string} currentStep
 * @param {string} activeTool
 * @param {string[]} stepToolIds from STEP_CONFIGS[step].tools
 */
export function getToolHelpPanel(currentStep, activeTool, stepToolIds) {
  if (activeTool && activeTool !== 'none' && TOOL_HELP[activeTool]) {
    return { mode: 'single', entry: TOOL_HELP[activeTool] };
  }

  const extras = STEP_EXTRA_HELP[currentStep] || [];
  const seen = new Set();
  const toolEntries = [];

  for (const id of stepToolIds || []) {
    if (seen.has(id) || !TOOL_HELP[id]) continue;
    seen.add(id);
    toolEntries.push({ toolId: id, ...TOOL_HELP[id] });
  }

  const overview = STEP_OVERVIEW_FALLBACK[currentStep] || null;

  return {
    mode: 'list',
    extras,
    toolEntries,
    overview,
    stepLabel: currentStep,
  };
}
